import React, { useEffect, useMemo, useState } from 'react';
import {
    Paper,
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TextField,
    IconButton,
    Avatar,
    Button,
    Stack,
    Autocomplete,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import { Add, DeleteOutline, Save, Inventory2 } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import useCurrency from '../../../hooks/common/useCurrency';
import { useUpdateOrder } from '../../../hooks/orders/useUpdateOrder';
import axios from '../../../utils/axios';

const EDITABLE_STATUSES = new Set(['pending', 'confirmed']);

/**
 * Items editor for an order.
 *
 * The backend OrderItem resource includes these fields:
 *   { id, product_id, variant_id,
 *     name_snapshot: {en, ar}, sku_snapshot,
 *     qty, unit_price, discount_total, tax_total, line_total }
 *
 * On Save, we build the delta payload required by PUT /admin/orders/{id}.
 */
const OrderItemsEditor = ({ order, onSaved }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng.startsWith('ar');
    const { format: formatPrice } = useCurrency();

    const editable = EDITABLE_STATUSES.has(order?.status);

    const seedDraft = useMemo(
        () =>
            (order?.items || []).map((it) => ({
                id: it.id,
                product_id: it.product_id ?? it.product?.id,
                variant_id: it.variant_id ?? it.variant?.id ?? null,
                name: it.name_snapshot || it.product?.name || null,
                sku:  it.sku_snapshot  || it.variant?.sku || it.product?.sku || null,
                image: it.product?.image?.thumb || it.product?.image_url || null,
                qty: it.qty,
                unit_price: it.unit_price ?? 0,
                discount: it.discount_total ?? 0,
                tax: it.tax_total ?? 0,
                line_total: it.line_total ?? 0,
                _delete: false,
                _new: false,
            })),
        [order]
    );

    const [draft, setDraft] = useState(seedDraft);
    useEffect(() => setDraft(seedDraft), [seedDraft]);

    const [productSearch, setProductSearch] = useState('');
    const [productOptions, setProductOptions] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    const { updateOrderMutate, isUpdating } = useUpdateOrder();

    // Empty search loads the latest products; typing filters them.
    useEffect(() => {
        if (!editable) return;
        let cancelled = false;
        const handle = setTimeout(() => {
            setLoadingProducts(true);
            axios
                .get('/admin/products', {
                    params: {
                        search: productSearch || undefined,
                        per_page: 20,
                    },
                })
                .then((res) => {
                    if (!cancelled) setProductOptions(res.data?.data || []);
                })
                .catch(() => {})
                .finally(() => {
                    if (!cancelled) setLoadingProducts(false);
                });
        }, productSearch ? 300 : 0);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [productSearch, editable]);

    const dirty = useMemo(() => {
        if (draft.length !== seedDraft.length) return true;
        return draft.some((d, i) => {
            const s = seedDraft[i];
            if (!s) return true;
            return (
                d.qty !== s.qty ||
                Number(d.unit_price) !== Number(s.unit_price) ||
                Number(d.discount) !== Number(s.discount) ||
                d._delete || d._new
            );
        });
    }, [draft, seedDraft]);

    const updateItem = (index, patch) => {
        setDraft((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
    };

    const removeItem = (index) => {
        setDraft((prev) => {
            const item = prev[index];
            if (item._new) return prev.filter((_, i) => i !== index);
            return prev.map((it, i) => (i === index ? { ...it, _delete: true } : it));
        });
    };

    const addItem = (product) => {
        if (!product) return;
        setDraft((prev) => [
            ...prev,
            {
                id: null,
                product_id: product.id,
                variant_id: null,
                name: product.name || null,
                sku: product.sku || null,
                image: product.image?.thumb || product.image_url || null,
                qty: 1,
                unit_price: product.price ?? 0,
                discount: 0,
                tax: 0,
                line_total: product.price ?? 0,
                _delete: false,
                _new: true,
            },
        ]);
        setProductSearch('');
        setProductOptions([]);
    };

    const visibleDraft = draft.filter((d) => !d._delete);

    const handleSave = () => {
        if (!dirty) return;
        if (visibleDraft.length === 0) {
            alert(isArabic ? 'يجب أن يحتوي الطلب على عنصر واحد على الأقل.' : 'Order must have at least one item.');
            return;
        }

        const items = draft
            .map((d) => {
                if (d._delete && d.id) return { id: d.id, _delete: true };
                if (d._new) {
                    return {
                        id: null,
                        product_id: d.product_id,
                        variant_id: d.variant_id,
                        qty: d.qty,
                        unit_price_override: Number(d.unit_price) || 0,
                        discount: Number(d.discount) || 0,
                    };
                }
                return {
                    id: d.id,
                    qty: d.qty,
                    unit_price_override: Number(d.unit_price) || 0,
                    discount: Number(d.discount) || 0,
                };
            })
            .filter(Boolean);

        updateOrderMutate(
            { id: order.id, data: { items } },
            { onSuccess: () => onSaved?.() }
        );
    };

    const resolveName = (n) => {
        if (!n) return '-';
        if (typeof n === 'string') return n;
        return n[lng] || n.en || n.ar || '-';
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                mb: 3,
                overflow: 'hidden',
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Typography variant="h6" fontWeight={700}>
                    {isArabic ? 'منتجات الطلب' : 'Order Items'}
                </Typography>
                {editable && (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<Save />}
                        onClick={handleSave}
                        disabled={!dirty || isUpdating}
                        sx={{ borderRadius: 2, fontWeight: 'bold' }}
                    >
                        {isUpdating
                            ? (isArabic ? 'جاري الحفظ...' : 'Saving...')
                            : (isArabic ? 'حفظ التغييرات' : 'Save Changes')}
                    </Button>
                )}
            </Stack>

            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 760 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>
                                {isArabic ? 'المنتج' : 'Product'}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, width: 90 }}>
                                {isArabic ? 'الكمية' : 'Qty'}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, width: 120 }}>
                                {isArabic ? 'السعر' : 'Unit Price'}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, width: 110 }}>
                                {isArabic ? 'خصم' : 'Discount'}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, width: 100 }}>
                                {isArabic ? 'ضريبة' : 'Tax'}
                            </TableCell>
                            <TableCell align={isArabic ? 'left' : 'right'} sx={{ fontWeight: 700, width: 130 }}>
                                {isArabic ? 'الإجمالي' : 'Line Total'}
                            </TableCell>
                            {editable && <TableCell sx={{ width: 56 }} />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleDraft.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={editable ? 7 : 6} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {isArabic ? 'لا توجد عناصر' : 'No items'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}

                        {draft.map((row, idx) => {
                            if (row._delete) return null;
                            const productName = resolveName(row.name);
                            // For new rows we compute a preview (server will recalc on save).
                            // For existing rows we trust the server's line_total since it
                            // already includes tax and other adjustments.
                            const lineTotal = row._new
                                ? (Number(row.unit_price) || 0) * (Number(row.qty) || 0) - (Number(row.discount) || 0)
                                : (row.line_total ?? 0);

                            return (
                                <TableRow key={`${row.id || 'new'}-${idx}`} hover>
                                    <TableCell sx={{ minWidth: 240 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar
                                                src={row.image || undefined}
                                                variant="rounded"
                                                sx={{ width: 44, height: 44, bgcolor: 'action.hover' }}
                                            >
                                                <Inventory2 fontSize="small" />
                                            </Avatar>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                    sx={{
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                        lineHeight: 1.3,
                                                    }}
                                                >
                                                    {productName}
                                                </Typography>
                                                {row.sku && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        SKU: {row.sku}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>

                                    <TableCell align="center">
                                        {editable ? (
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={row.qty}
                                                onChange={(e) =>
                                                    updateItem(idx, { qty: Math.max(1, parseInt(e.target.value || 1, 10)) })
                                                }
                                                inputProps={{ min: 1 }}
                                                sx={{ width: 70 }}
                                            />
                                        ) : (
                                            <Typography variant="body2" fontWeight="medium">{row.qty}</Typography>
                                        )}
                                    </TableCell>

                                    <TableCell align="center">
                                        {editable ? (
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={row.unit_price}
                                                onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                                                inputProps={{ min: 0, step: '0.01' }}
                                                sx={{ width: 100 }}
                                            />
                                        ) : (
                                            <Typography variant="body2">{formatPrice(row.unit_price)}</Typography>
                                        )}
                                    </TableCell>

                                    <TableCell align="center">
                                        {editable ? (
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={row.discount}
                                                onChange={(e) => updateItem(idx, { discount: e.target.value })}
                                                inputProps={{ min: 0, step: '0.01' }}
                                                sx={{ width: 90 }}
                                            />
                                        ) : (
                                            <Typography variant="body2" color={row.discount > 0 ? 'error.main' : 'text.secondary'}>
                                                {row.discount > 0 ? `- ${formatPrice(row.discount)}` : '-'}
                                            </Typography>
                                        )}
                                    </TableCell>

                                    <TableCell align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {formatPrice(row.tax || 0)}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align={isArabic ? 'left' : 'right'}>
                                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                                            {formatPrice(lineTotal)}
                                        </Typography>
                                    </TableCell>

                                    {editable && (
                                        <TableCell align="center" sx={{ width: 56 }}>
                                            <Tooltip title={isArabic ? 'حذف' : 'Remove'}>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => removeItem(idx)}
                                                >
                                                    <DeleteOutline />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}

                        {editable && (
                            <TableRow>
                                <TableCell colSpan={editable ? 7 : 6} sx={{ borderBottom: 0, pt: 2 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Add fontSize="small" color="action" />
                                        <Autocomplete
                                            sx={{ flex: 1 }}
                                            size="small"
                                            options={productOptions}
                                            loading={loadingProducts}
                                            getOptionLabel={(o) =>
                                                o?.name?.[lng] || o?.name?.en || o?.name?.ar || o?.name || ''
                                            }
                                            onInputChange={(_, val) => setProductSearch(val)}
                                            onChange={(_, val) => addItem(val)}
                                            renderInput={(p) => (
                                                <TextField
                                                    {...p}
                                                    placeholder={
                                                        isArabic
                                                            ? 'ابحث عن منتج لإضافته...'
                                                            : 'Search a product to add...'
                                                    }
                                                    InputProps={{
                                                        ...p.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {loadingProducts && <CircularProgress size={18} />}
                                                                {p.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default OrderItemsEditor;
